// Turn liveness — the pure, TRANSIENT reducer that owns "is a turn running
// (drafting) vs settled (idle)" per conversation, plus the stopped-turn fence
// (IDD 58538994, .claude/analysis/dogfood-2026-09-09/D0-chatDecider-analysis.md).
//
// Why it lives in @bilda/app, not the UI: liveness is turn-lifecycle DECISION
// logic (idle → drafting → terminal), and the D-0 investigation found the UI
// grew the ONLY owner of it — the durable `Conversation` aggregate is a flat
// entry list with no turn/drafting status, and the S2 Turn projection carries
// turn GROUPING, not liveness. A hand-maintained per-arm transition function in
// the view tree is the exact ui-as-projection violation the doc names, and it
// drifted twice in the 2026-09-09 dogfood (the connection-lost arm never
// cleared `drafting`, masked only because a sibling terminal folded first).
// Concentrating every terminal arm here gives BOTH shells + the tests ONE arm-
// set, so a terminal can never again clear liveness in one shell and forget in
// another. The UI PROJECTS `drafting`/the fence off this reducer, fed the same
// transport results (POST outcome, SSE progress, transient deltas) it fed the
// old per-arm code.
//
// TRANSIENT by construction (ADR — IDD 58538994): NO durable
// `turn_began`/`turn_settled` events. Liveness is a session-scoped, in-memory
// fact — a reload correctly rehydrates a settled transcript with `drafting`
// false (the held-open POST is gone), so inventing a durable turn-event pair
// would be a larger, separate lifecycle decision (couples to the durable-turn
// PM + reconnect work) and is deliberately NOT smuggled in here.
//
// Purity (dev-ts, pure-core/impure-shell): no I/O, no clock, no id-minting, no
// React. Named leaf types on a `type` discriminant, summed; `evolve` ends in
// `exhaustSwitchCase`. Reached by consumers as `App.TurnLiveness.*`.
import { exhaustSwitchCase } from "@bilda/utils";
export const initial = {
    status: new Map(),
    cancelledMessageIds: new Set(),
};
// === Selectors — what the UI PROJECTS =========================================
// Is a turn currently in flight in this conversation? The projected `drafting`.
export const isDrafting = (state, conversationId) => state.status.get(conversationId) === "drafting";
// Is this message id fenced (its turn was stopped or lost)? The late-event gate.
export const isFenced = (state, messageId) => state.cancelledMessageIds.has(messageId);
// === evolve — the total reducer ==============================================
const withStatus = (state, conversationId, status) => ({
    ...state,
    status: new Map(state.status).set(conversationId, status),
});
const withFence = (state, ids) => ({
    ...state,
    cancelledMessageIds: new Set([...state.cancelledMessageIds, ...ids]),
});
export const evolve = (state, event) => {
    switch (event.type) {
        case "turn_began":
            return withStatus(state, event.conversationId, "drafting");
        case "turn_landed":
        case "turn_failed":
            return withStatus(state, event.conversationId, "idle");
        case "turn_stopped":
        case "turn_lost_connection":
            // The one place stop and connection-loss share: settle AND fence.
            return withFence(withStatus(state, event.conversationId, "idle"), event.cancelledMessageIds);
        default:
            return exhaustSwitchCase(event);
    }
};
//# sourceMappingURL=turnLiveness.js.map